import { computed } from 'mobx';

import type { TConstraints } from '@feathr/rachis';

import { EIntegrationTypes } from '../integrations';
import type { CampaignClass } from '.';
import type { TPublisherPlatforms } from './facebook';
import type { ITTDCampaignAttributes } from './ttd_base';
import { TTDCampaign } from './ttd_base';

export type TOmniIntegration = EIntegrationTypes.Tradedesk | EIntegrationTypes.Meta;

export interface IMetaSettings {
  page_id: string;
  publisher_platforms: TPublisherPlatforms[];
  special_ad_category: 'NONE' | 'HOUSING' | 'EMPLOYMENT' | 'FINANCIAL_PRODUCTS_SERVICES';
}

// Pixl Plus
export interface IOmniCampaignAttributes extends ITTDCampaignAttributes {
  readonly _cls: CampaignClass.Omni;
  integrations?: TOmniIntegration[];
  meta_settings?: IMetaSettings;
}

export class OmniCampaign extends TTDCampaign<IOmniCampaignAttributes> {
  public override get constraints(): TConstraints<IOmniCampaignAttributes> {
    return {
      ...super.constraints,
      integrations: {
        presence: {
          allowEmpty: false,
          message: '^Select at least one channel to proceed',
        },
      },
      meta_settings: {
        requiredFields: {
          unless: (attributes) => !attributes.integrations?.includes(EIntegrationTypes.Meta),
          fields: {
            page_id: {
              message: '^Meta requires selecting a Facebook page',
            },
            publisher_platforms: {
              message: '^Meta requires selecting publisher platforms',
            },
            special_ad_category: {
              message: '^Meta requires selecting a special ad category',
            },
          },
        },
      },
      exposure_settings: {
        requiredFields: {
          unless: (attributes) => {
            const integrations = attributes.integrations ?? [];
            const isDual =
              integrations.includes(EIntegrationTypes.Tradedesk) &&
              integrations.includes(EIntegrationTypes.Meta);
            if (!isDual) {
              return true;
            }
            const channelSplit = attributes.exposure_settings?.channel_split;
            return !!channelSplit?.allocations?.length;
          },
          fields: {
            channel_split: {
              message: '^Configure a budget split between Display and Meta channels',
            },
          },
        },
      },
    };
  }

  /** Whether both Display (TTD) and Meta channels are selected. */
  @computed
  public get isDualChannel(): boolean {
    const integrations: TOmniIntegration[] = this.get('integrations') ?? [];
    return (
      integrations.includes(EIntegrationTypes.Tradedesk) &&
      integrations.includes(EIntegrationTypes.Meta)
    );
  }

  /** Whether only Meta channel is selected. */
  @computed
  public get isMetaChannelOnly(): boolean {
    const integrations: TOmniIntegration[] = this.get('integrations') ?? [];
    return integrations.length === 1 && integrations.includes(EIntegrationTypes.Meta);
  }

  /** Whether the Meta channel is selected, whether on its own or alongside Display. */
  @computed
  public get hasMetaChannel(): boolean {
    const integrations: TOmniIntegration[] = this.get('integrations') ?? [];
    return integrations.includes(EIntegrationTypes.Meta);
  }

  /**
   * Whether the campaign has a Meta special ad category set.
   *
   * Meta's API rejects lookalike audiences and restricts targeting options when a
   * special ad category (Housing, Employment, Financial Products & Services) is set.
   */
  @computed
  public get hasSpecialAdCategory(): boolean {
    const specialAdCategory = this.get('meta_settings')?.special_ad_category;
    return specialAdCategory !== undefined && specialAdCategory !== 'NONE';
  }

  public override getDefaults(): Partial<IOmniCampaignAttributes> {
    return {
      ...super.getDefaults(),
    };
  }
}
